import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { 
  TRAVEL_SEARCH, 
  ENV,
  API_KEYS,
  CDN,
} from 'utils/constants'
import { 
  GET_OCEAN_CRUISES,
  ADD_TO_SEARCH_HISTORY,
  GET_LOCATIONS,
  GET_FLIGHT_OFFERS,
  GET_CHEAPEST_FLIGHT,
  GET_FLIGHT_PRICE_ANALYSIS,
  GET_FLIGHT_INSPIRATION,
} from 'api/constants'
import {
  setFlightOptions,
  clearFlightOptions,
  setHotelOptions,
  clearHotelOptions,
  setLongStayOptions,
  clearLongStayOptions,
  setRentalCarOptions,
  clearRentalCarOptions,
  setOceanCruiseOptions,
  clearOceanCruiseOptions,
  setRiverCruiseOptions,
  clearRiverCruiseOptions,
  setExperienceOptions,
  addToExperienceOptionList,
  toggleExperienceOptionListItem,
  clearExperienceOptionList,
} from 'slices/travelSlice'
import { cloneDeep, isEmpty, find, sumBy, xor } from 'lodash'
import { formatDate } from 'utils/helpers'
import Geocode from 'react-geocode'
import { createEvents } from 'ics'
import md5 from 'md5'
import moment from 'moment/moment'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Type from 'components/Type'
import { 
  useDebouncedCallback, 
  useScript, 
  useUniqueID,
  useQuery,
} from 'utils/hooks'
import { useAction } from 'utils/actions'
import { useGTM } from 'utils/hooks'
import { openBackgroundTab } from 'utils/helpers'
import {
  toggleAffiliateFavorite,
} from 'slices/globalSlice'
import { ReactComponent as IconCelExperience } from 'icons/CelExperience.svg'
import { ReactComponent as IconCelLocation } from 'icons/CelLocation.svg'
import { ReactComponent as IconCelMagnifyingGlass } from 'icons/CelMagnifyingGlass.svg'
import { ReactComponent as IconCelCar } from 'icons/CelCar.svg'
import { ReactComponent as IconCelCruise } from 'icons/CelCruise.svg'
import { ReactComponent as IconCelFlight } from 'icons/CelFlight.svg'
import { ReactComponent as IconCelStay } from 'icons/CelStay.svg'

export const useAffiliate = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { openModal } = useAction()
  const user = useSelector(({ global }) => global.user)
  const { fireGTM } = useGTM({ event: 'Affiliate' })
  const {
    bank_actual_total,
    donde_credits_total,
    company_credits_total,
  } = useSelector(({ global }) => global.savingsTotals) || {}
  const currentSavings = +bank_actual_total || 0
  const currentCompanyPoints = +company_credits_total || 0
  const currentPoints = +donde_credits_total || 0
  const hasPoints = !!+donde_credits_total
  const hasCompanyPoints = !!+company_credits_total
  const spendingPower = currentSavings + currentCompanyPoints + currentPoints

  const onOpenAffiliate = (vendor, isOffer) => {
      fireGTM({ affiliateName: vendor.title, page: 'affiliatePage' })
      openBackgroundTab(`${vendor.url}${vendor.provider === 'EBG' ? `${vendor.url.includes('?') ? '&' : '?'}login=1&_xcid=${user.unique_id}` : ''}`)
      // if (!user.has_savings_account && user.is_wallet_capable && !spendingPower) {
      //   fireGTM({ affiliateName: vendor.title, page: 'affiliatePage' })
      //   openBackgroundTab(`${vendor.url}${vendor.provider === 'EBG' ? `&login=1&_xcid=${user.unique_id}` : ''}`)
      //   openModal(<CreateWallet />)
      // } else {
      //   const variant = find(vendor?.tags, { name: 'stay' }) ? 'stay' : find(vendor?.tags, { name: 'ground transportation' }) ? 'car' : undefined
      //   openModal(
      //     <HowToBook 
      //       {...vendor} 
      //       variant={variant ? variant : isOffer && 'offer'}
      //     />
      //   )
      // }
    }

  return {
    onAffiliateClick: (vendor, isOffer) => {
      if (vendor?.unique_id) {
        navigate(`/shop-travel/travel-partners/${vendor?.unique_id}`)
      } else {
        onOpenAffiliate(vendor, isOffer)
      }
    },
    onOpenAffiliate,
    onToggleAffiliateFavorite: (unique_id, newValue) => {
      dispatch(toggleAffiliateFavorite({
        unique_id,
        newValue,
      }))
    }
  }
}

Geocode.setApiKey(API_KEYS.PLACES)

export const usePlacesSearch = () => {
  const dispatch = useDispatch()
  const generatedID = useUniqueID()
  const id = generatedID
  const status = useScript(CDN.PLACES.URL)
  const cities = useSelector(({ global }) => global.locationsSearchResults) || []

  const getPlacesOptionLabel = option => {
    return typeof option === 'string' ? option : [
      option.structured_formatting?.main_text, 
      option.structured_formatting?.secondary_text,
    ].filter(item => item?.length).join(', ')
  }

  const executePlacesSearch = useDebouncedCallback(search => {
    if (search?.length) {
      dispatch({ 
        type: GET_LOCATIONS, 
        search, 
        stoken: id,
      })
    }
  }, 200)

  return {
    getPlacesOptionLabel,
    executePlacesSearch,
    cities,
  }
}

const searchTypes = [
  'airport',
  'lodging',
  'restaurant',
  'cafe',
  'bar',
  'museum',
  'park',
  'art_gallery',
  'tourist_attraction',
  'zoo',
  'aquarium',
  'car_rental',
  'travel_agency',
  'transit_station',
  'train_station',
  'bus_station',
  'subway_station',
  'shopping_mall',
  'supermarket',
  'night_club',
  'movie_theater',
  'spa',
  // 'gym',
  // 'embassy',
  // 'bank',
  // 'atm',
  'hospital',
  // 'pharmacy',
  // 'insurance_agency',
  // 'police',
  // 'church',
  // 'mosque',
  // 'synagogue',
  // 'hindu_temple',
];


export const useNearbySearch = map => {
  const dispatch = useDispatch()
  const generatedID = useUniqueID()
  const id = generatedID
  useScript(CDN.PLACES.URL)

  const findNearbyPOI = useCallback((type, radius = 500) => {
    return new Promise((resolve, reject) => {
      if (!map || !window.google || !window.google.maps || !window.google.maps.places) {
        reject(new Error('Map object or Google Maps API is not available'))
        return
      }

      const bounds = map.getBounds()
      const center = bounds.getCenter()

      const service = new window.google.maps.places.PlacesService(map)
      const request = {
        location: { lat: center.lat(), lng: center.lng() },
        radius,
        type,
      }

      service.nearbySearch(request, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          resolve(results)
        } else {
          console.warn('Places search failed', status)
        }
      })
    })
  }, [map])

  return {
    findNearbyPOI,
    searchTypes,
  }
}

const DEBUG = false

const FLIGHT_TEMPLATE = {
  arrive_airport: {},
  depart_airport: {},
  depart_date: null,
  return_date: null,
}

const CRUISE_LENGTH = {
  any: { value: 'Any cruise length', min: 0, max: 365 },
  // nights_1_2: { value: '1–2 nights', min: 1, max: 2 },
  nights_3_6: { value: '3–6 nights', min: 3, max: 6 },
  nights_7_9: { value: '7–9 nights', min: 7, max: 9 },
  nights_10_14: { value: '10–14 nights', min: 10, max: 14 },
  nights_14_plus: { value: 'More than 14 nights', min: 14, max: -1 },
}

const normalizeDate = (date, format) => {
  let temp = date
  if (typeof date === 'string') temp = moment(temp)
  return temp?.format?.(format)
}

export const useFlightSearch = () => {
  const dispatch = useDispatch()
  const flightOptions = useSelector(({ travel }) => travel.flightOptions) || {}
  const [isLoading, setIsLoading] = useState(false)
  const eToken = useSelector(({ global }) => global.eToken)
  const { home_airport } = useSelector(({ global }) => global.user)
  const initialSetup = useRef(false)

  const {
    flight_type,
    number_adults,
    number_seniors,
    number_youth,
    number_children,
    number_infants_in_seat,
    number_infants_in_lap,
    flight_class,
    depart_time,
    return_time,
    ages,
  } = flightOptions

  const flights = flightOptions.flights?.length
    ? flightOptions.flights
    : flightOptions.flight_type === 'MC'
    ? [FLIGHT_TEMPLATE, FLIGHT_TEMPLATE]
    : [FLIGHT_TEMPLATE]

  const childArr = new Array(number_children).fill(null)
  const youthArr = new Array(number_youth).fill(null)

  let isDisabled = false

  flights.forEach(flight => {
    if (
      !!!flight.arrive_airport?.iata_code?.length ||
      !!!flight.depart_airport?.iata_code?.length ||
      !flight.depart_date
    )
      isDisabled = true
  })

  if (
    flight_type === 'RT' &&
    (flights[0]?.depart_date > flights[0]?.return_date ||
      !flights[0]?.return_date)
  )
    isDisabled = true

  const total_passengers =
    number_adults +
    number_seniors +
    number_youth +
    number_children +
    number_infants_in_seat +
    number_infants_in_lap
  const total_children = number_children + number_youth

  if (Object.keys(ages).length < total_children) isDisabled = true

  const searchFlights = skipSave => {
    if (!skipSave) {
      dispatch({
        type: ADD_TO_SEARCH_HISTORY,
        data: { type: 'Flight', ...flightOptions },
        hash: md5(`Flight${  flights[flights.length - 1].arrive_airport.key_value}`),
      })
    }
    setIsLoading(true)
    const form = new FormData()

    form.append('search_params[type]', flight_type)
    form.append('search_params[adults]', number_adults)
    form.append('search_params[seniors]', number_seniors)
    form.append('search_params[youth_selector]', number_youth)
    Object.keys(ages)?.map((age, idx) => {
      form.append(`search_params[children][${idx}][age]`, ages[age])
    })
    form.append('search_params[infant_in_lap]', number_infants_in_lap)
    form.append('search_params[infant_in_seat]', number_infants_in_seat)
    form.append('search_params[class]', flight_class === 'E_Restricted' ? 'E' : flight_class)
    if (flight_class === 'E_Restricted') form.append('search_params[exclude_restricted]', 'on')

    if (flight_type === 'MC') {
      flights.forEach((flight, idx) => {
        form.append(`search_params[legs][${idx}][depart_airport]`, flight.depart_airport.iata_code)
        form.append(`search_params[legs][${idx}][arrive_airport]`, flight.arrive_airport.iata_code)
        form.append(`search_params[legs][${idx}][depart_date]`, normalizeDate(flight.depart_date, 'MM/DD/YYYY'))
        form.append(`search_params[legs][${idx}][depart_time]`, depart_time)
      })
    } else {
      form.append('search_params[legs][0][depart_airport]', flights[0].depart_airport.iata_code)
      form.append('search_params[legs][0][arrive_airport]', flights[0].arrive_airport.iata_code)
      form.append('search_params[legs][0][depart_date]', normalizeDate(flights[0].depart_date, 'MM/DD/YYYY'))
      form.append('search_params[legs][0][depart_time]', depart_time)

      if (flight_type === 'RT') {
        form.append('search_params[legs][1][depart_airport]', flights[0].arrive_airport.iata_code)
        form.append('search_params[legs][1][arrive_airport]', flights[0].depart_airport.iata_code)
        form.append('search_params[legs][1][depart_date]', normalizeDate(flights[0].return_date, 'MM/DD/YYYY'))
        form.append('search_params[legs][1][depart_time]', return_time)
      }
    }
    form.append('customer_parameters[donde_env]', ENV)
    if (!DEBUG) form.append('edata', eToken)
    const queryString = new URLSearchParams(form).toString()
    if (!DEBUG) window.open(`${TRAVEL_SEARCH.FLIGHT}&${queryString}`, '_self')
    if (DEBUG) console.warn('Flight Search: ', queryString)
  }

  const clearFlightOptionsUtil = useCallback(() => {
    initialSetup.current = true
    dispatch(clearFlightOptions())
  }, [])

  const addFlight = (data = {}) => {
    dispatch(setFlightOptions({ flights: [...flights, { ...FLIGHT_TEMPLATE, ...data }] }))
  }

  const addOptionToFlight = (index, data) => {
    let temp = cloneDeep(flights)
    temp.splice(index, 1, {
      ...flights[index],
      ...data,
    })
    dispatch(setFlightOptions({ flights: temp }))
  }

  const removeFlight = index => {
    let temp = cloneDeep(flightOptions)
    temp.flights.splice(index, 1)
    dispatch(setFlightOptions(temp))
  }

  const changeTripType = useCallback(key => {
    let temp = []
    if (home_airport?.length) {
      temp.push({
        ...FLIGHT_TEMPLATE,
        depart_airport: initialSetup.current ? {} : { iata_code: home_airport },
      })
      if (flight_type === 'MC') {
        temp.push({
          ...FLIGHT_TEMPLATE,
          arrive_airport: initialSetup.current ? {} : { iata_code: home_airport },
        })
      }
    } else {
      temp =
        flightOptions.flight_type === 'MC'
          ? [FLIGHT_TEMPLATE, FLIGHT_TEMPLATE]
          : [FLIGHT_TEMPLATE]
    }
    dispatch(setFlightOptions({ flights: temp, flight_type: key }))
  }, [home_airport])

  return {
    flightOptions: {
      flights,
      ...flightOptions
    }, 
    total_passengers,
    total_children,
    childArr,
    youthArr,
    isDisabled,
    isLoading,
    changeTripType,
    searchFlights,
    addFlightOption: data => dispatch(setFlightOptions(data)),
    clearFlightOptions: clearFlightOptionsUtil,
    addFlight,
    addOptionToFlight,
    removeFlight,
  }
}

export const useHotelSearch = ({
  getParams,
} = {}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const query = useQuery()
  const [isLoading, setIsLoading] = useState(false)
  const eToken = useSelector(({ global }) => global.eToken)
  const hotelOptions = useSelector(({ travel }) => travel.hotelOptions)

  const {
    isDateRangeValid,
    destination,
    revelex_id,
    check_in_date,
    check_out_date,
    rooms,
  } = hotelOptions

  const getHotelParams = data => `check_in_date=${data?.check_in_date || check_in_date || query.get('check_in_date')}&check_out_date=${data?.check_out_date || check_out_date || query.get('check_out_date')}&destination=${data?.destination || destination || query.get('destination')}`

  let isDisabled =
    !destination?.length ||
    !isDateRangeValid

  const addHotelOption = data => dispatch(setHotelOptions(data))

  const clearHotelOptionsUtil = () => dispatch(clearHotelOptions())

  const onDateRangeChange = data => {
    const isPast = moment(data[0]) < moment()
    addHotelOption({
      check_in_date: isPast ? null : moment(data[0])?.format?.('YYYY-MM-DD') || null,
      check_out_date: isPast ? null : moment(data[1])?.format?.('YYYY-MM-DD') || null,
      isDateRangeValid: moment(data[0]) < moment(data[1]) && !isPast,
    })
  }

  const updateRoom = (index, data) => {
    let temp = cloneDeep(rooms)
    temp.splice(index, 1, {
      ...rooms[index],
      ...data,
    })
    addHotelOption({ rooms: temp })
  }

  const getDestinationLabel = data => {
    if (typeof data === 'string') return data
    if (data?.search_data?.type === 'Stay') return getName(data?.search_data)
    return (data?.description || data?.name || [data?.city, data?.state_short, data?.country_code].filter(item => item?.length).join(', '))
  }

  const createSearchString = () => {
    const form = new FormData()
    form.append('customer_parameters[donde_env]', ENV)
    form.append('search[name]', destination)
    form.append('search[check_in_date]', normalizeDate(check_in_date, 'MM/DD/YYYY'))
    form.append('search[check_out_date]', normalizeDate(check_out_date, 'MM/DD/YYYY'))
    if (!DEBUG) form.append('edata', eToken)

    rooms.forEach((room, idx) => {
      form.append(
        `search[rooms][${idx + 1}][number_adults]`,
        room.number_adults
      )
      form.append(
        `search[rooms][${idx + 1}][number_children]`,
        Object.keys(room.ages).length
      )
      for (let i = 0; i < room.number_children; i++) {
        form.append(
          `search[rooms][${idx + 1}][children_ages][${i}]`,
          room.ages[`child_${i}`]
        )
      }
    })

    if (revelex_id) {
      form.append('search[hotel_list][revelex_hotel_id][]', revelex_id)
      const queryString = new URLSearchParams(form).toString()
      if (!DEBUG) window.open(`${TRAVEL_SEARCH.HOTEL_DEEP}&${queryString}`, '_self')
      if (DEBUG) console.warn('Hotel Search: ', queryString)
    } else {
      setIsLoading(true)
      // Get latitude & longitude from address.
      Geocode.fromAddress(destination).then(
        response => {
          const { lat, lng } = response.results[0].geometry.location
          form.append('search[latitude]', lat)
          form.append('search[longitude]', lng)
          const queryString = new URLSearchParams(form).toString()
          if (!DEBUG) window.open(`${TRAVEL_SEARCH.HOTEL}&${queryString}`, '_self')
          if (DEBUG) console.warn('Hotel Search: ', queryString)
        },
        error => {
          setIsLoading(false)
          console.error(error)
        }
      )
    }
  }

  const searchHotels = ({ skipSave, search_data } = {}) => {
    if (!skipSave) {
      dispatch({
        type: ADD_TO_SEARCH_HISTORY,
        data: { type: 'Stay', ...hotelOptions },
        hash: md5(`Stay${  destination}`),
      })
    }
    if (search_data) {
      addHotelOption(search_data)
      onDateRangeChange([search_data?.check_in_date, search_data?.check_out_date])
    }
    navigate(`/shop-travel/stays/hotels?${getHotelParams({ check_in_date: search_data?.check_in_date, check_out_date: search_data?.check_out_date, destination: search_data?.destination })}`)
  }

  useEffect(() => {
    if (getParams) {
      if (query.get('check_in_date') && query.get('check_out_date')) {
        onDateRangeChange([query.get('check_in_date'), query.get('check_out_date')])
      }
      if (query.get('destination')) {
        addHotelOption({ destination: query.get('destination'), inputValue: query.get('destination') })
      }
    }
  }, [query, getParams])

  return {
    hotelOptions,
    totalAdults: sumBy(rooms, o => o.number_adults),
    totalChildren: sumBy(rooms, o => o.number_children), 
    isLoading,
    isDisabled,
    searchHotels,
    getDestinationLabel,
    onDateRangeChange,
    addHotelOption,
    getHotelParams,
    clearHotelOptions: clearHotelOptionsUtil,
    updateRoom,
  }
}

export const travelLeisureUnitCodes = {
  H: 'Hotel',
  S: 'Studio',
  '-1': 'Hotel',
  0: 'Studio',
  1: '1 Bedroom',
  2: '2 Bedroom',
  3: '3 Bedroom',
  4: '4 Bedroom',
}

export const useLongStaySearch = ({
  getParams,
} = {}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const query = useQuery()
  const longStayOptions = useSelector(({ travel }) => travel.longStayOptions)

  const {
    isDateRangeValid,
    destination,
    check_in_date,
    check_out_date,
    rooms,
    
    region_id,
    lat,
    lng,
    unit_filter,
    amenity_filter,
    activity_filter,
    exclude_all_inclusive_filter,
  } = longStayOptions

  const getLongStayParams = data => {
    const params = new URLSearchParams()
    const addParam = (key, value) => {
      if (value !== 'null' && value !== null && value !== '') params.set(key, value)
    }
    addParam('check_in_date', data?.check_in_date || check_in_date || query.get('check_in_date'))
    addParam('check_out_date', data?.check_out_date || check_out_date || query.get('check_out_date'))
    addParam('destination', data?.destination || destination || query.get('destination'))
    addParam('region_id', data?.region_id || region_id || query.get('region_id'))
    addParam('lat', data?.lat || lat || query.get('lat'))
    addParam('lng', data?.lng || lng || query.get('lng'))
    addParam('exclude_all_inclusive_filter', data?.exclude_all_inclusive_filter ?? exclude_all_inclusive_filter ?? query.get('exclude_all_inclusive_filter'))
    if (data?.unit_filter?.length === 0) {
      params.set('unit_filter', '')
    } else {
      addParam('unit_filter', data?.unit_filter?.join?.(',') || unit_filter?.join?.(',') || query.get('unit_filter'))
    }
    if (data?.amenity_filter?.length === 0) {
      params.set('amenity_filter', '')
    } else {
      addParam('amenity_filter', data?.amenity_filter?.join?.(',') || amenity_filter?.join?.(',') || query.get('amenity_filter'))
    }
    if (data?.activity_filter?.length === 0) {
      params.set('activity_filter', '')
    } else {
      addParam('activity_filter', data?.activity_filter?.join?.(',') || activity_filter?.join?.(',') || query.get('activity_filter'))
    }
    return params.toString() ? `?${params.toString()}` : ''
  }

  let isDisabled =
    !isDateRangeValid

  const addLongStayOption = data => dispatch(setLongStayOptions(data))

  const clearLongStayOptionsUtil = () => dispatch(clearLongStayOptions())

  const onDateRangeChange = data => {
    const isPast = moment(data[0]) < moment()
    addLongStayOption({
      check_in_date: isPast ? null : moment(data[0])?.format?.('MM/DD/YYYY') || null,
      check_out_date: isPast ? null : moment(data[1])?.format?.('MM/DD/YYYY') || null,
      isDateRangeValid: moment(data[0]) < moment(data[1]) && !isPast,
    })
  }

  const getDestinationLabel = data => {
    if (typeof data === 'string') return data
    if (data?.search_data?.type === 'LongStay') return getName(data?.search_data)
    return (data?.description || data?.name || [data?.city, data?.state_short, data?.country_code].filter(item => item?.length).join(', '))
  }

  const searchLongStays = ({ skipSave, search_data } = {}) => {
    if (!skipSave) {
      dispatch({
        type: ADD_TO_SEARCH_HISTORY,
        data: { type: 'LongStay', ...longStayOptions },
        hash: md5(`Stay${  destination}`),
      })
    }
    if (search_data) {
      addLongStayOption(search_data)
      onDateRangeChange([search_data?.check_in_date, search_data?.check_out_date])
    }
    navigate(`/shop-travel/long-stays/results?${getLongStayParams({ check_in_date: search_data?.check_in_date, check_out_date: search_data?.check_out_date, destination: search_data?.destination })}`)
  }

  const xorFilter = (key, filter) => {
    const temp = cloneDeep(longStayOptions[`${key}_filter`])
    const index = temp.indexOf(filter)
    index === -1 ? temp.push(filter) : temp.splice(index, 1)
    return temp
    // addLongStayOption({ [key]: temp })
  }

  useEffect(() => {
    if (getParams) {
      const data = {}
      if (query.get('check_in_date') && query.get('check_out_date')) {
        onDateRangeChange([query.get('check_in_date'), query.get('check_out_date')])
      } else {
        const start = moment().add(1, 'month')
        const end =  moment(start).add(2, 'month')
        onDateRangeChange([start.format('MM/DD/YYYY'), end.format('MM/DD/YYYY')])
      }
      if (query.get('destination')) data.destination = query.get('destination')
      // console.log('fff', query.get('unit_filter').split(','))
      if (query.get('region_id')) data.region_id = query.get('region_id')
      if (query.get('lat')) data.lat = query.get('lat')
      if (query.get('lng')) data.lng = query.get('lng')
      if (query.get('exclude_all_inclusive_filter')) data.exclude_all_inclusive_filter = query.get('exclude_all_inclusive_filter') === 'true'
      data.unit_filter = query.get('unit_filter') ? query.get('unit_filter').split(',') : []
      data.amenity_filter = query.get('amenity_filter') ? query.get('amenity_filter').split(',') : []
      data.activity_filter = query.get('activity_filter') ? query.get('activity_filter').split(',') : []
      addLongStayOption(data)
    }
  }, [query, getParams])

  return {
    longStayOptions,
    isDisabled,
    xorFilter,
    searchLongStays,
    getDestinationLabel,
    onDateRangeChange,
    addLongStayOption,
    getLongStayParams,
    clearLongStayOptions: clearLongStayOptionsUtil,
  }
}

export const useCarSearch = () => {
  const dispatch = useDispatch()
  const rentalCarOptions = useSelector(({ travel }) => travel.rentalCarOptions) || {}
  const eToken = useSelector(({ global }) => global.eToken)
  const [isLoading, setIsLoading] = useState(false)

  const {
    pick_up_location,
    drop_off_location,
    pick_up_date,
    drop_off_date,
    dropoff_type,
  } = rentalCarOptions

  const isDisabled =
    (dropoff_type === 'different' ? !drop_off_location?.iata_code : false) ||
    !pick_up_location?.iata_code ||
    pick_up_date > drop_off_date ||
    !pick_up_date ||
    !drop_off_date

  const searchCars = skipSave => {
    if (!skipSave) {
      dispatch({
        type: ADD_TO_SEARCH_HISTORY,
        data: { type: 'Car', ...rentalCarOptions },
        hash: md5(`Car${  pick_up_location}`),
      })
    }
    setIsLoading(true)

    const form = new FormData()

    form.append('search[pick_up_location]', pick_up_location.iata_code)
    form.append('search[pick_up_date]', normalizeDate(pick_up_date, 'MM/DD/YYYY'))
    form.append('search[pick_up_time]', normalizeDate(pick_up_date, 'HH:mm'))
    form.append(
      'search[drop_off_location]',
      dropoff_type === 'different'
        ? drop_off_location.iata_code
        : pick_up_location.iata_code
    )
    form.append('search[drop_off_date]', normalizeDate(drop_off_date, 'MM/DD/YYYY'))
    form.append('search[drop_off_time]',  normalizeDate(drop_off_date, 'HH:mm'))
    form.append('customer_parameters[donde_env]', ENV)
    if (!DEBUG) form.append('edata', eToken)
    const queryString = new URLSearchParams(form).toString()
    if (!DEBUG) window.open(`${TRAVEL_SEARCH.CAR}&${queryString}`, '_self')
    if (DEBUG) console.warn('Car Search: ', queryString)
  }

  return {
    rentalCarOptions, 
    isLoading,
    isDisabled,
    searchCars,
    addCarOption: data => dispatch(setRentalCarOptions(data)),
    clearCarOptions: () => dispatch(clearRentalCarOptions()),
  }
}

export const useOceanCruiseSearch = ({
  disableSearch,
} = {}) => {
  const dispatch = useDispatch()
  const oceanCruiseOptions = useSelector(({ travel }) => travel.oceanCruiseOptions) || {}
  const oceanCruisesSearchResults = useSelector(({ global }) => global.oceanCruisesSearchResults) || []
  const [isLoading, setIsLoading] = useState(false)
  const eToken = useSelector(({ global }) => global.eToken)

  const {
    search_type,
    start_date,
    vendor,
    cruise_length,
    destination,
  } = oceanCruiseOptions

  const isDisabled = !destination?.destination_id?.length || !cruise_length || !start_date

  const searchOceanCruises = skipSave => {
    if (!skipSave) {
      dispatch({
        type: ADD_TO_SEARCH_HISTORY,
        data: { type: 'Ocean Cruise', ...oceanCruiseOptions },
        hash: md5(`Ocean Cruise${  destination.name}`),
      })
    }
    setIsLoading(true)
    const form = new FormData()
    form.append('search[search_type]', search_type)
    form.append('search[start_date]', normalizeDate(start_date, 'MM/DD/YYYY'))
    form.append('search[destination_ids]', destination.destination_id)
    if (vendor.vendor_id !== -1) form.append('search[vendor_ids][]', vendor.vendor_id)
    form.append('search[duration_min]', find(CRUISE_LENGTH, { value: cruise_length })?.min)
    form.append('search[duration_max]', find(CRUISE_LENGTH, { value: cruise_length })?.max)
    form.append('customer_parameters[donde_env]', ENV)
    if (!DEBUG) form.append('edata', eToken)
    const queryString = new URLSearchParams(form).toString()
    if (!DEBUG) window.open(`${TRAVEL_SEARCH.OCEAN_CRUISE}&${queryString}`, '_self')
    if (DEBUG) console.warn('Ocean Cruise: ', queryString)
  }

  useEffect(() => {
    if (isEmpty(oceanCruisesSearchResults) && !disableSearch) dispatch({ type: GET_OCEAN_CRUISES, search: '' })
  }, [disableSearch])

  return {
    oceanCruiseOptions, 
    oceanCruisesSearchResults,
    isLoading,
    isDisabled,
    searchOceanCruises,
    addOceanCruiseOption: data => dispatch(setOceanCruiseOptions(data)),
    clearOceanCruiseOptions: () => dispatch(clearOceanCruiseOptions()),
  }
}

export const useRiverCruiseSearch = () => {
  const dispatch = useDispatch()
  const riverCruiseOptions = useSelector(({ travel }) => travel.riverCruiseOptions) || {}
  const eToken = useSelector(({ global }) => global.eToken)
  const [isLoading, setIsLoading] = useState(false)

  const {
    country,
    city,
    river,
    ship,
    search_by,
  } = riverCruiseOptions

  let isDisabled = false
  if (search_by === '') isDisabled = true
  if (search_by === 'country') isDisabled = !country?.country_code?.length && !country?.name?.length
  if (search_by === 'city') isDisabled = !city?.name?.length
  if (search_by === 'river') isDisabled = !river?.name?.length
  if (search_by === 'ship') isDisabled = !ship?.name?.length

  const searchRiverCruises = skipSave => {
    if (!skipSave) {
      dispatch({
        type: ADD_TO_SEARCH_HISTORY,
        data: { type: 'River Cruise', ...riverCruiseOptions },
        hash: md5(`River Cruise${  riverCruiseOptions[search_by].name}`),
      })
    }
    setIsLoading(true)
    const form = new FormData()
    if (search_by === 'country') form.append('search[country_codes][]', country.country_code || country.name)
    if (search_by === 'city') form.append('search[geolocation_ids][]', city.name)
    if (search_by === 'river') form.append('search[waterway_ids][]', river.name)
    if (search_by === 'ship') form.append('search[ship_id][]', ship.name)
    form.append('customer_parameters[donde_env]', ENV)
    if (!DEBUG) form.append('edata', eToken)
    const queryString = new URLSearchParams(form).toString()
    if (!DEBUG) window.open(`${TRAVEL_SEARCH.RIVER_CRUISE}&${queryString}`, '_self')
    if (DEBUG) console.warn('River Cruise: ', queryString)
  }

  return {
    riverCruiseOptions, 
    isLoading,
    isDisabled,
    searchRiverCruises,
    addRiverCruiseOption: data => dispatch(setRiverCruiseOptions(data)),
    clearRiverCruiseOptions: () => dispatch(clearRiverCruiseOptions()),
  }
}


const IconCelSearchStyled = () => 
  <Box
    sx={{
      display: 'inline-flex',
      placeItems: 'center',
      placeContent: 'center',
      width: 40,
      height: 40,
    }}
  >
    <IconCelMagnifyingGlass
      viewBox="0 0 40 40"
      width="30"
      height="30"
    />
  </Box>

export const getImage = o => {
  try {
    if (o.type === 'Search') return <IconCelSearchStyled />
    if (o.type === 'Stay') return <IconCelStay viewBox="0 0 20 19" height="30" width="30" />
    if (o.type === 'Experience') {
      return o.value?.previewImage?.url ? 
        <Box
          sx={{
            backgroundImage: `url(${o.value?.previewImage?.url})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderRadius: '10px',
            width: 40,
            height: 40,
            overflow: 'hidden',
          }}
        /> : 
        o.value?.value ? 
        <IconCelExperience 
          viewBox="0 0 27 17"
          width="40"
          height="40"
        /> : 
        o.value?.description ? 
        <IconCelLocation 
          viewBox="0 0 39 55"
          width="40"
          height="40"
        /> : 
        <IconCelExperience 
          viewBox="0 0 27 17"
          width="40"
          height="40"
        />
    }
    if (o.type === 'Car') return <IconCelCar viewBox="0 0 30 14" width="40" height="40" />
    if (o.type === 'Ocean Cruise' || o.type === 'River Cruise') return <IconCelCruise viewBox="0 0 18 20" width="40" height="40" />
    if (o.type === 'Flight') return <IconCelFlight viewBox="0 0 23 23" width="40" height="40" />
  } catch (e) {
    return ''
  }
}

export const getName = o => {
  try {
    if (o.type === 'Search') return o.value
    if (o.type === 'Stay') return o.destination
    if (o.type === 'Experience') {
      const text = o.value?.structured_formatting?.main_text || o.value?.name || o.value?.value
      return typeof text === 'string' ? text : 'N/A'
    }
    if (o.type === 'Car') return o.pick_up_location.name
    if (o.type === 'Ocean Cruise') return o.destination.name
    if (o.type === 'River Cruise') return o[o.search_by].name
    if (o.type === 'Flight') return o.flights[o.flights.length - 1].arrive_airport.key_value
  } catch (e) {
    return ''
  }
}

export const getDetails = o => {
  try {
    if (o.type === 'Search') return null
    if (o.type === 'Stay') return `${formatDate(o.check_in_date, 'LONG_NO_WEEKDAY')} — ${formatDate(o.check_out_date, 'LONG_NO_WEEKDAY')}`
    if (o.type === 'Experience') {
      const holiText = o.value?.place?.cityName ? o.value?.place?.cityName : undefined
      // const holiText = o.value?.place?.cityName ? `${o.value?.place?.cityName}, ${o.value?.place?.countryName}` : undefined
      const text = o.value?.structured_formatting?.secondary_text || holiText || ''
      return text
    }
    if (o.type === 'Car') return `${formatDate(o.pick_up_date, 'LONG_NO_WEEKDAY')} — ${formatDate(o.drop_off_date, 'LONG_NO_WEEKDAY')}`
    if (o.type === 'Ocean Cruise') return `${formatDate(o.start_date, 'LONG_NO_WEEKDAY')}`
    if (o.type === 'River Cruise') return null
    if (o.type === 'Flight') {
      const returnDate = o.flights[o.flights.length - 1].return_date
      return `${formatDate(o.flights[0].depart_date, 'LONG_NO_WEEKDAY')}${returnDate ? ` — ${formatDate(returnDate, 'LONG_NO_WEEKDAY')}` : ''}`
    }
  } catch (e) {
    return ''
  }
}

export const useExperienceSearch = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const experienceOptions = useSelector(({ travel }) => travel.experienceOptions) || {}
  // const [isLoading, setIsLoading] = useState(false)

  const {
    selectedOptions,
  } = experienceOptions

  // let isDisabled = false

  const handleGetResults = (query, skipSave) => {
    if (query && !query?.skipSave && !skipSave) {
      dispatch({
        type: ADD_TO_SEARCH_HISTORY,
        data: { type: 'Experience', value: { value: query } },
        hash: md5(`Experience${query}`),
      })
    }
    navigate(`/experiences/things-to-do?q=${query?.value || query}`)
  }

  const searchExperiences = ({ value, skipSave }) => {
    if (value?.description) {
      Geocode.fromAddress(value.description).then(
        response => {
          const { lat, lng } = response.results[0].geometry.location
          if (!value.skipSave && !skipSave) {
            dispatch({
              type: ADD_TO_SEARCH_HISTORY,
              data: { type: 'Experience', value: value },
              hash: md5(`Experience${lat}${lng}`),
            })
          }
          navigate(`/experiences/things-to-do?lat=${lat}&lng=${lng}&location_name=${value.description}${value.query ? `&q=${value.query}` : ''}`)
        },
        error => {
          console.error(error)
        }
      )
    } else if (value?.id) {
      if (!value.skipSave && !skipSave) {
        dispatch({
          type: ADD_TO_SEARCH_HISTORY,
          data: { type: 'Experience', value: value },
          hash: md5(`Experience${value.id}`),
        })
      }
      navigate(`/experience/things-to-do/${value.id}`)
    } else {
      handleGetResults(value || '', skipSave)
    }
  }

  const addExperienceOption = useCallback(data => dispatch(setExperienceOptions(data)), [])

  const clearExperienceOptions = useCallback(() => dispatch(clearExperienceOptions()), [])

  const addToOptionList = useCallback(data => dispatch(addToExperienceOptionList(data)), [])

  const toggleOptionListItem = useCallback(data => dispatch(toggleExperienceOptionListItem(data)), [])

  const clearOptionList = useCallback(() => dispatch(clearExperienceOptionList()), [])

  return {
    experienceOptions, 
    // isLoading,
    // isDisabled,
    searchExperiences,
    handleGetResults,
    addExperienceOption,
    clearExperienceOptions,
    addToOptionList,
    toggleOptionListItem,
    clearOptionList,
    isOptionActive: value => find(selectedOptions, { value }),
  }
}

export const getSearchHistoryElement = (data, showChip) => {
  const details = getDetails(data)

  return (
    <Stack
      sx={theme => theme.internalMixins.columns({ justify: 'flex-start', gap: 1 })}
    >
      <Box
        sx={{
          display: 'inline-flex',
          placeItems: 'center',
          placeContent: 'center',
          width: 40,
          height: 40,
        }}
      >
        {getImage(data)}
      </Box>
      <Stack>
        <Type
          variant="dataSmall"
          color="neutralCool_700"
          // lines="2"
        >
          {getName(data)}
        </Type>
        {!!details && (
          <Type
            variant="dataMicro"
            color="neutralCool_700"
            blend={7}
          >
            {details}
          </Type>
        )}
      </Stack>
      {/* {showChip && (
        <Chip
          variant="secondary_b500"
          size="short"
          duoTone
          font="labelSubAtomicMedium"
        >
          {data.type}
        </Chip>
      )} */}
    </Stack>
  )
}

const getAddressComponent = (addressComponents, componentType) => {
    const component = addressComponents.find(comp => comp.types.includes(componentType))
    return component ? component.long_name : null
}

export const useGoogleAddress = address => {
  const [result, setResult] = useState({})

  useEffect(() => {
    if (address?.length) {
      Geocode.fromAddress(address).then(
          response => {
            const { lat, lng } = response.results[0].geometry.location
            setResult({
              lat,
              lng,
              ...response.results[0],
              city: getAddressComponent(response.results[0]?.address_components, 'locality'),
              state: getAddressComponent(response.results[0]?.address_components, 'administrative_area_level_1'),
              country: getAddressComponent(response.results[0]?.address_components, 'country'),
              postal_code: getAddressComponent(response.results[0]?.address_components, 'postal_code'),
            })
          },
          error => {
            setResult({})
            console.error(error)
          }
        )
    }
  }, [address])

  return result
}

export const useFlights = () => {
  const dispatch = useDispatch()
  const flightOffers = useSelector(({ global }) => global.flightOffers)
  const cheapestFlight = useSelector(({ global }) => global.cheapestFlight)
  const flightPriceAnalysis = useSelector(({ global }) => global.flightPriceAnalysis)
  const flightInspiration = useSelector(({ global }) => global.flightInspiration)
  
  useEffect(() => {
    dispatch({
      type: GET_FLIGHT_OFFERS,
    })
    // dispatch({
    //   type: GET_CHEAPEST_FLIGHT,
    // })
    dispatch({
      type: GET_FLIGHT_PRICE_ANALYSIS,
    })
    dispatch({
      type: GET_FLIGHT_INSPIRATION,
    })
  }, [])

  return useMemo(() => ({
    flightOffers,
    cheapestFlight,
    flightPriceAnalysis,
    flightInspiration,
  }), [
    flightOffers,
    cheapestFlight,
    flightPriceAnalysis,
    flightInspiration,
  ])
}

export const downloadIcsFile = async (events, config = {}) => {
  // createEvents(events, (error, value) => {
  //   if (error) {
  //     console.log(error)
  //     return
  //   }

  //   const blob = new Blob([value], { type: 'text/calendar' })
  //   const url = URL.createObjectURL(blob)

  //   const link = document.createElement('a')
  //   link.href = url
  //   link.download = `${config.name || 'calendar'}.ics`
  //   document.body.appendChild(link)
  //   link.click()
  //   document.body.removeChild(link)

  //   URL.revokeObjectURL(url)
  // })

  const filename = `${config.name || 'calendar'}.ics`
  const file = await new Promise((resolve, reject) => {
    createEvents(events, (error, value) => {
      if (error) reject(error)
      resolve(new File([value], filename, { type: 'text/calendar' }))
    })
    // createEvent(event, (error, value) => {
    //   if (error) {
    //     reject(error)
    //   }

    //   resolve(new File([value], filename, { type: 'text/calendar' }))
    // })
  })
  const url = URL.createObjectURL(file)

  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename

  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)

  URL.revokeObjectURL(url)
}

export const useSortedStays = stays => {
  const sortedStays = useMemo(() => {

    if (!stays?.hotels || !stays) return []

    return Object.values(stays?.hotels || stays).sort((a, b) => {
      let cheapestA = null
      a.availabilities?.forEach(o => {
        o.roomRates.forEach(o1 => {
          if (cheapestA === null || cheapestA.averageBeforeTax > o1.averageBeforeTax) cheapestA = o1
        })
      })
      let cheapestB = null
      b.availabilities?.forEach(o => {
        o.roomRates.forEach(o1 => {
          if (cheapestB === null || cheapestB.averageBeforeTax > o1.averageBeforeTax) cheapestB = o1
        })
      })

      const weightA = (a.review > 5 ? a.review / 2 : a.review) - Math.log(cheapestA.averageBeforeTax)
      const weightB = (b.review > 5 ? b.review / 2 : b.review) - Math.log(cheapestB.averageBeforeTax)

      if (weightA === weightB) return cheapestA.averageBeforeTax - cheapestB.averageBeforeTax

      return weightB - weightA
    }) || []
  }, [stays])

  return sortedStays
}
